import React, { ReactNode, useEffect, useRef, useState } from "react";

const OTP_LENGTH = 6;

type TOTPInputProps = {
  length?: number;
  label?: string | ReactNode;
  disabled?: boolean;
  onOtpComplete?: (value: string) => void;
};

export default function OTPInput({
  length = OTP_LENGTH,
  label = "Enter OTP",
  disabled,
  onOtpComplete,
}: TOTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const otpString = otp.join("");
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const focusOnNext = (index: number) => {
    inputRefs.current[index + 1]?.focus();
  };

  const focusOnPrevious = (index: number) => {
    inputRefs.current[index - 1]?.focus();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value } = e.target;
    if (!/^\d?$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to next input if a digit is entered
    if (value && index < OTP_LENGTH - 1) {
      focusOnNext(index);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        // Move focus to previous input if empty
        focusOnPrevious(index);
      }

      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusOnPrevious(index);
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusOnNext(index);
    }
  };

  useEffect(() => {
    if (otpString.length === length) {
      onOtpComplete?.(otpString);
    }
  }, [otp, length]); // Run only when `otp` changes

  return (
    <div className="flex-col gap-8">
      {typeof label === "string" ? (
        <label className="fw-400 text-center fs-link fw-500">{label}</label>
      ) : (
        label
      )}
      <div className="flex jc-center gap-1">
        {otp.map((digit, index) => (
          <input
            disabled={disabled}
            key={index}
            ref={(el) => {
              if (el) inputRefs.current[index] = el;
            }}
            type="tel"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            style={{
              maxWidth: 50,
              minWidth: 0,
              height: 40,
              border: "1px solid var(--clr-white-400)",
            }}
            className="text-center flex-grow-1 flex-center p-1 br-4 outline-none taptap-focus-ring"
          />
        ))}
      </div>
    </div>
  );
}
